import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import ValidUserContext from "../authCheck";
import classes from "./UserAdmin.module.scss";
import {
  createAdminUser,
  deleteAdminUser,
  fetchAdminUsers,
  fetchImportableUsers,
  importTableauUsers,
  sendAdminWelcomeEmail,
  setAdminUserStatus,
  updateAdminUser,
} from "./ApiService";

const EMPTY_FORM = {
  user_email: "",
  display_name: "",
  description: "",
  security_groups: [],
  convert_to_portal_auth: false,
};

const formatDate = (value) => {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Never";
  return parsed.toLocaleString();
};

const isTableauAdmin = (siteRole) =>
  Boolean(siteRole && siteRole.includes("Administrator"));

const userGroups = (user) => {
  if (Array.isArray(user.security_groups) && user.security_groups.length) {
    return user.security_groups;
  }
  if (user.security_group) return [user.security_group];
  return [];
};

const unavailableGroupsFor = (user) => {
  if (Array.isArray(user.unavailable_security_groups)) {
    return user.unavailable_security_groups;
  }
  return [];
};

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

const SORT_GETTERS = {
  email: (user) => (user.user_email || "").toLowerCase(),
  display_name: (user) => (user.display_name || "").toLowerCase(),
  description: (user) => (user.description || "").toLowerCase(),
  security_groups: (user) => userGroups(user).join(", ").toLowerCase(),
  tableau_role: (user) => (user.tableau_site_role || "").toLowerCase(),
  status: (user) => (user.is_active ? "active" : "disabled"),
  last_login: (user) => {
    if (!user.last_login) return 0;
    const parsed = Date.parse(user.last_login);
    return Number.isNaN(parsed) ? 0 : parsed;
  },
};

const UserAdmin = () => {
  const validUserContext = useContext(ValidUserContext);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyEmail, setBusyEmail] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [form, setForm] = useState(EMPTY_FORM);
  const [staleGroups, setStaleGroups] = useState([]);
  const [editingIsAdmin, setEditingIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importable, setImportable] = useState([]);
  const [importSelected, setImportSelected] = useState([]);
  const [importSearch, setImportSearch] = useState("");
  const [importConvertAuth, setImportConvertAuth] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importError, setImportError] = useState("");
  const [sortKey, setSortKey] = useState("email");
  const [sortDir, setSortDir] = useState("asc");

  const availableSet = new Set(groups);

  const sortedUsers = useMemo(() => {
    const getter = SORT_GETTERS[sortKey] || SORT_GETTERS.email;
    const direction = sortDir === "asc" ? 1 : -1;
    return [...users].sort((left, right) => {
      const compared = compareValues(getter(left), getter(right));
      if (compared !== 0) return compared * direction;
      return (left.user_email || "").localeCompare(right.user_email || "");
    });
  }, [users, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const sortHeader = (key, label) => {
    const active = sortKey === key;
    return (
      <th
        aria-sort={
          active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
        }
      >
        <button
          type="button"
          className={classes.sortBtn}
          onClick={() => toggleSort(key)}
        >
          {label}
          <span className={classes.sortIndicator} aria-hidden="true">
            {active ? (sortDir === "asc" ? "▲" : "▼") : ""}
          </span>
        </button>
      </th>
    );
  };

  const filteredImportable = useMemo(() => {
    const query = importSearch.trim().toLowerCase();
    if (!query) return importable;
    return importable.filter(
      (user) =>
        user.user_email.toLowerCase().includes(query) ||
        (user.display_name || "").toLowerCase().includes(query)
    );
  }, [importable, importSearch]);

  const loadUsers = useCallback(async ({ refresh } = {}) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminUsers({ refresh });
      setUsers(data.users || []);
      setGroups(data.groups || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openCreateForm = () => {
    setFormMode("create");
    setForm(EMPTY_FORM);
    setStaleGroups([]);
    setEditingIsAdmin(false);
    setNotice("");
    setError("");
    setFormOpen(true);
  };

  const openEditForm = (user) => {
    const assigned = userGroups(user);
    const unavailable = unavailableGroupsFor(user);
    const unavailableSet = new Set(unavailable);
    setFormMode("edit");
    setForm({
      user_email: user.user_email,
      display_name: user.display_name || "",
      description: user.description || "",
      // Only assignable groups go in the form; stale ones are shown read-only.
      security_groups: assigned.filter(
        (name) => !unavailableSet.has(name) && availableSet.has(name)
      ),
      convert_to_portal_auth: false,
    });
    setStaleGroups(unavailable);
    setEditingIsAdmin(isTableauAdmin(user.tableau_site_role));
    setNotice("");
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(EMPTY_FORM);
    setStaleGroups([]);
    setEditingIsAdmin(false);
  };

  const openImportModal = async () => {
    setImportOpen(true);
    setImportSelected([]);
    setImportSearch("");
    setImportConvertAuth(false);
    setImportError("");
    setImportLoading(true);
    try {
      const data = await fetchImportableUsers();
      setImportable(data.users || []);
    } catch (err) {
      setImportError(err.message);
      setImportable([]);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setImportOpen(false);
    setImportable([]);
    setImportSelected([]);
    setImportSearch("");
    setImportConvertAuth(false);
    setImportError("");
  };

  const toggleImportUser = (email) => {
    setImportSelected((current) =>
      current.includes(email)
        ? current.filter((value) => value !== email)
        : [...current, email]
    );
  };

  const toggleImportAllFiltered = () => {
    const emails = filteredImportable.map((user) => user.user_email);
    const allSelected = emails.every((email) => importSelected.includes(email));
    if (allSelected) {
      setImportSelected((current) => current.filter((email) => !emails.includes(email)));
    } else {
      setImportSelected((current) => Array.from(new Set([...current, ...emails])));
    }
  };

  const submitImport = async () => {
    if (!importSelected.length) return;
    if (
      importConvertAuth &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        "Convert selected users to Portal Users authentication in Tableau? Their current Tableau sign-in method will change."
      )
    ) {
      return;
    }

    const selectedSet = new Set(importSelected);
    const payloadUsers = importable
      .filter((user) => selectedSet.has(user.user_email))
      .map((user) => ({
        user_email: user.user_email,
        display_name: user.display_name,
        security_groups: user.is_admin ? [] : user.security_groups || [],
      }));

    setImportSaving(true);
    setImportError("");
    setError("");
    setNotice("");
    try {
      const result = await importTableauUsers(payloadUsers, importConvertAuth);
      const failed = (result.results || []).filter((item) => !item.ok);
      const importedCount = result.imported_count || 0;
      const parts = [
        `Imported ${importedCount} user${importedCount === 1 ? "" : "s"}`,
        `default portal password "${result.default_password}"`,
      ];
      if (result.welcome_emails_sent) {
        parts.push(
          `welcome email sent to ${result.welcome_emails_sent}`
        );
      }
      if (result.convert_to_portal_auth) {
        parts.push("converted Tableau auth to Portal Users");
      }
      if (failed.length) {
        parts.push(
          `${failed.length} failed: ${failed
            .map((item) => `${item.user_email || "unknown"} (${item.error})`)
            .join("; ")}`
        );
      }
      setNotice(`${parts[0]} with ${parts[1]}${parts.length > 2 ? `. ${parts.slice(2).join(". ")}` : "."}`);
      closeImportModal();
      await loadUsers();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportSaving(false);
    }
  };

  const updateField = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const toggleGroup = (groupName) => {
    setForm((current) => {
      const selected = current.security_groups.includes(groupName)
        ? current.security_groups.filter((name) => name !== groupName)
        : [...current.security_groups, groupName];
      return { ...current, security_groups: selected };
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    const payload = {
      user_email: form.user_email,
      display_name: form.display_name,
      description: form.description,
    };
    if (!editingIsAdmin) {
      payload.security_groups = form.security_groups;
    }
    if (formMode === "edit" && form.convert_to_portal_auth && !editingIsAdmin) {
      payload.convert_to_portal_auth = true;
    }
    try {
      if (formMode === "create") {
        const result = await createAdminUser(payload);
        const warnings = (result.warnings || []).join(" ");
        const welcomeNote = result.welcome_email_sent
          ? "A welcome email was sent."
          : "";
        setNotice(
          `${result.user_email} created with the default password "${result.default_password}". ${welcomeNote} ${warnings}`.trim()
        );
      } else {
        const result = await updateAdminUser(payload);
        const warnings = (result.warnings || []).join(" ");
        setNotice(`${result.user_email} updated. ${warnings}`.trim());
      }
      closeForm();
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendWelcomeEmail = async () => {
    if (!form.user_email) return;
    setBusyEmail(form.user_email);
    setError("");
    setNotice("");
    try {
      await sendAdminWelcomeEmail(form.user_email);
      setNotice(`Welcome email sent to ${form.user_email}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyEmail("");
    }
  };

  const toggleStatus = async (user) => {
    const nextActive = !user.is_active;
    const action = nextActive ? "reactivate" : "disable";
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        nextActive
          ? `Reactivate ${user.user_email} and restore Viewer access in Tableau?`
          : `Disable ${user.user_email} and set them to Unlicensed in Tableau?`
      )
    ) {
      return;
    }

    setBusyEmail(user.user_email);
    setError("");
    setNotice("");
    try {
      const result = await setAdminUserStatus(user.user_email, nextActive);
      const warnings = (result.warnings || []).join(" ");
      setNotice(`${user.user_email} ${nextActive ? "reactivated" : "disabled"}. ${warnings}`.trim());
      await loadUsers();
    } catch (err) {
      setError(`Could not ${action} ${user.user_email}: ${err.message}`);
    } finally {
      setBusyEmail("");
    }
  };

  const impersonate = async (user) => {
    setBusyEmail(user.user_email);
    setError("");
    setNotice("");
    try {
      await validUserContext.startImpersonation(user.user_email);
    } catch (err) {
      setError(`Could not impersonate ${user.user_email}: ${err.message}`);
      setBusyEmail("");
    }
  };

  const deleteUser = async (user) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Permanently delete ${user.user_email} from the portal and Tableau? This cannot be undone.`
      )
    ) {
      return;
    }

    setBusyEmail(user.user_email);
    setError("");
    setNotice("");
    try {
      const result = await deleteAdminUser(user.user_email);
      const warnings = (result.warnings || []).join(" ");
      setNotice(`${user.user_email} deleted. ${warnings}`.trim());
      if (formOpen && form.user_email === user.user_email) {
        closeForm();
      }
      await loadUsers();
    } catch (err) {
      setError(`Could not delete ${user.user_email}: ${err.message}`);
    } finally {
      setBusyEmail("");
    }
  };

  return (
    <div className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <h1 className={classes.title}>User Management</h1>
          <p className={classes.subtitle}>
            Portal accounts and their Tableau security groups.
          </p>
        </div>
        <div className={classes.headerActions}>
          <button
            type="button"
            className={classes.secondaryBtn}
            onClick={() => loadUsers({ refresh: true })}
          >
            Refresh
          </button>
          <button type="button" className={classes.secondaryBtn} onClick={openImportModal}>
            Import from Tableau
          </button>
          <button type="button" className={classes.primaryBtn} onClick={openCreateForm}>
            New user
          </button>
        </div>
      </div>

      {error && <div className={classes.error}>{error}</div>}
      {notice && <div className={classes.notice}>{notice}</div>}

      {formOpen && (
        <form className={classes.form} onSubmit={submitForm}>
          <div className={classes.formGrid}>
            <label className={classes.field}>
              <span>Email</span>
              <input
                type="email"
                value={form.user_email}
                onChange={updateField("user_email")}
                disabled={formMode === "edit"}
                required
              />
            </label>
            <label className={classes.field}>
              <span>Display name</span>
              <input
                type="text"
                value={form.display_name}
                onChange={updateField("display_name")}
              />
            </label>
            <label className={`${classes.field} ${classes.descriptionField}`}>
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={updateField("description")}
                rows={3}
                placeholder="Admin notes (not shown to the user)"
              />
            </label>
            <fieldset
              className={`${classes.field} ${classes.groupField}`}
              disabled={editingIsAdmin}
            >
              <legend>Security groups</legend>
              <div
                className={`${classes.checkboxList} ${
                  editingIsAdmin ? classes.checkboxListDisabled : ""
                }`}
              >
                {groups.map((group) => (
                  <label key={group} className={classes.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={!editingIsAdmin && form.security_groups.includes(group)}
                      onChange={() => toggleGroup(group)}
                      disabled={editingIsAdmin}
                    />
                    <span>{group}</span>
                  </label>
                ))}
              </div>
              {editingIsAdmin && (
                <p className={classes.groupHint}>
                  Tableau administrators are not assigned to security groups.
                  Their dashboards come from a single shared folder.
                </p>
              )}
              {!editingIsAdmin && staleGroups.length > 0 && (
                <div className={classes.staleGroups}>
                  <span className={classes.staleGroupsLabel}>
                    Assigned but no longer available in the portal
                  </span>
                  <div className={classes.groupList}>
                    {staleGroups.map((group) => (
                      <span
                        key={group}
                        className={`${classes.groupChip} ${classes.groupChipUnavailable}`}
                        title="This group is still assigned but was removed from portal Security Groups"
                      >
                        {group}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </fieldset>
            {formMode === "edit" && !editingIsAdmin && (
              <label className={`${classes.checkboxItem} ${classes.convertAuth}`}>
                <input
                  type="checkbox"
                  checked={form.convert_to_portal_auth}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      convert_to_portal_auth: event.target.checked,
                    }))
                  }
                />
                <span>
                  Convert Tableau authentication to Portal Users
                  <span className={classes.convertHint}>
                    {" "}
                    (changes their Tableau sign-in method)
                  </span>
                </span>
              </label>
            )}
            {formMode === "edit" && editingIsAdmin && (
              <p className={`${classes.convertHint} ${classes.convertAuth}`}>
                Tableau administrator authentication is managed in Tableau and
                cannot be converted here.
              </p>
            )}
          </div>

          <div className={classes.formActions}>
            <button type="button" className={classes.secondaryBtn} onClick={closeForm}>
              Cancel
            </button>
            {formMode === "edit" && (
              <button
                type="button"
                className={classes.secondaryBtn}
                disabled={saving || busyEmail === form.user_email}
                onClick={sendWelcomeEmail}
              >
                {busyEmail === form.user_email ? "Sending…" : "Send welcome email"}
              </button>
            )}
            <button type="submit" className={classes.primaryBtn} disabled={saving}>
              {saving ? "Saving…" : formMode === "create" ? "Create user" : "Save changes"}
            </button>
          </div>
        </form>
      )}

      {importOpen && (
        <div className={classes.modalBackdrop} role="presentation" onClick={closeImportModal}>
          <div
            className={classes.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-tableau-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={classes.modalHeader}>
              <div>
                <h2 id="import-tableau-title" className={classes.title}>
                  Import from Tableau
                </h2>
                <p className={classes.subtitle}>
                  Create portal accounts for existing Tableau users. Tableau
                  authentication is left unchanged unless you opt in below.
                </p>
              </div>
              <button type="button" className={classes.secondaryBtn} onClick={closeImportModal}>
                Close
              </button>
            </div>

            {importError && <div className={classes.error}>{importError}</div>}

            <div className={classes.importToolbar}>
              <input
                type="search"
                className={classes.importSearch}
                placeholder="Search by email or name"
                value={importSearch}
                onChange={(event) => setImportSearch(event.target.value)}
              />
              <button
                type="button"
                className={classes.linkBtn}
                onClick={toggleImportAllFiltered}
                disabled={!filteredImportable.length}
              >
                {filteredImportable.length &&
                filteredImportable.every((user) =>
                  importSelected.includes(user.user_email)
                )
                  ? "Clear filtered"
                  : "Select filtered"}
              </button>
            </div>

            <label className={`${classes.checkboxItem} ${classes.convertAuth}`}>
              <input
                type="checkbox"
                checked={importConvertAuth}
                onChange={(event) => setImportConvertAuth(event.target.checked)}
              />
              <span>
                Convert selected users to Portal Users authentication
                <span className={classes.convertHint}>
                  {" "}
                  (changes Tableau sign-in method; skipped for administrators)
                </span>
              </span>
            </label>

            <div className={classes.importList}>
              {importLoading ? (
                <div className={classes.placeholder}>Loading Tableau users…</div>
              ) : filteredImportable.length === 0 ? (
                <div className={classes.placeholder}>
                  No importable Tableau users found.
                </div>
              ) : (
                filteredImportable.map((user) => (
                  <label key={user.user_email} className={classes.importRow}>
                    <input
                      type="checkbox"
                      checked={importSelected.includes(user.user_email)}
                      onChange={() => toggleImportUser(user.user_email)}
                    />
                    <span className={classes.importMain}>
                      <span className={classes.importEmail}>
                        {user.user_email}
                        {user.is_admin ? (
                          <span className={classes.importAdminBadge}>Admin</span>
                        ) : null}
                      </span>
                      <span className={classes.importMeta}>
                        {user.display_name}
                        {user.site_role ? ` · ${user.site_role}` : ""}
                        {user.auth_setting ? ` · ${user.auth_setting}` : ""}
                        {user.is_admin ? " · auth convert unavailable" : ""}
                      </span>
                      {!!(user.security_groups || []).length && (
                        <span className={classes.importMeta}>
                          Groups: {(user.security_groups || []).join(", ")}
                        </span>
                      )}
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className={classes.formActions}>
              <span className={classes.muted}>
                {importSelected.length} selected
              </span>
              <button type="button" className={classes.secondaryBtn} onClick={closeImportModal}>
                Cancel
              </button>
              <button
                type="button"
                className={classes.primaryBtn}
                onClick={submitImport}
                disabled={importSaving || !importSelected.length}
              >
                {importSaving ? "Importing…" : "Import selected"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={classes.tableWrapper}>
        {loading ? (
          <div className={classes.placeholder}>Loading users…</div>
        ) : users.length === 0 ? (
          <div className={classes.placeholder}>No portal users yet.</div>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                {sortHeader("email", "Email")}
                {sortHeader("display_name", "Display name")}
                {sortHeader("description", "Description")}
                {sortHeader("security_groups", "Security groups")}
                {sortHeader("tableau_role", "Tableau role")}
                {sortHeader("status", "Status")}
                {sortHeader("last_login", "Last login")}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => {
                const assigned = userGroups(user);
                const unavailable = new Set(unavailableGroupsFor(user));
                const userIsAdmin = isTableauAdmin(user.tableau_site_role);
                return (
                  <tr key={user.user_email}>
                    <td>{user.user_email}</td>
                    <td>{user.display_name}</td>
                    <td>
                      {user.description ? (
                        <span className={classes.descriptionCell} title={user.description}>
                          {user.description}
                        </span>
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {userIsAdmin ? (
                        <span className={classes.muted} title="Admins use a single shared folder">
                          Not applicable
                        </span>
                      ) : assigned.length ? (
                        <div className={classes.groupList}>
                          {assigned.map((group) => {
                            const isUnavailable =
                              unavailable.has(group) || !availableSet.has(group);
                            return (
                              <span
                                key={group}
                                className={`${classes.groupChip} ${
                                  isUnavailable ? classes.groupChipUnavailable : ""
                                }`}
                                title={
                                  isUnavailable
                                    ? "Assigned but no longer available in the portal"
                                    : group
                                }
                              >
                                {group}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={classes.muted}>None</span>
                      )}
                    </td>
                    <td>
                      {user.tableau_site_role || (
                        <span className={classes.muted}>Not in Tableau</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`${classes.badge} ${
                          user.is_active ? classes.badgeActive : classes.badgeDisabled
                        }`}
                      >
                        {user.is_active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td>{formatDate(user.last_login)}</td>
                    <td>
                      <div className={classes.rowActions}>
                        <button
                          type="button"
                          className={classes.linkBtn}
                          onClick={() => openEditForm(user)}
                        >
                          Edit
                        </button>
                        {user.is_active && !isTableauAdmin(user.tableau_site_role) && (
                          <button
                            type="button"
                            className={classes.linkBtn}
                            onClick={() => impersonate(user)}
                            disabled={busyEmail === user.user_email}
                          >
                            Impersonate
                          </button>
                        )}
                        {!isTableauAdmin(user.tableau_site_role) && (
                          <>
                            <button
                              type="button"
                              className={classes.linkBtn}
                              onClick={() => toggleStatus(user)}
                              disabled={busyEmail === user.user_email}
                            >
                              {user.is_active ? "Disable" : "Reactivate"}
                            </button>
                            <button
                              type="button"
                              className={classes.dangerBtn}
                              onClick={() => deleteUser(user)}
                              disabled={busyEmail === user.user_email}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserAdmin;
